import { db } from './firebase-init';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { createJob as createCutlistJob } from './cutlist-jobs';


// ── Map job order style label to cutlist type code ───────────────────────────
const STYLE_TO_CODE = {
  'double hung':       'DH',
  'horizontal slider': 'HS',
  'casement':          'CSMT',
  'picture window':    'PW',
  'basement hopper':   'BSMT',
  '3-lite slider':     '3HS',
  'brickmould':        'BM',
};
const TWP_LABELS = Object.keys(STYLE_TO_CODE);

function mapWindowType(style) {
  const d = (style || '').toLowerCase().trim();
  // Exact match first
  if (STYLE_TO_CODE[d]) return STYLE_TO_CODE[d];
  // Fuzzy match: check if style contains or is contained by a known key
  for (const [key, code] of Object.entries(STYLE_TO_CODE)) {
    if (d.includes(key) || key.includes(d)) return code;
  }
  // Additional aliases
  if (d.includes('3') && d.includes('lite') && (d.includes('slider') || d.includes('hs'))) return '3HS';
  if (d.includes('horizontal') || d.includes(' hs')) return 'HS';
  if (d.includes('double hung') || d.includes('dh')) return 'DH';
  if (d.includes('casement') || d.includes('csmt')) return 'CSMT';
  if (d.includes('picture') || d.includes(' pw')) return 'PW';
  if (d.includes('basement') || d.includes('hopper') || d.includes('bsmt')) return 'BSMT';
  if (d.includes('brickmould') || d.includes('brick mould') || d.includes(' bm')) return 'BM';
  return 'DH';
}

function isTWPWindow(style) {
  const d = (style || '').toLowerCase().trim();
  return TWP_LABELS.some(k => d.includes(k) || k.includes(d));
}

export function jobOrderWindowsToCutlist(joWindows, defaultColor, contractNumber) {
  let counter = 0;
  // HARDENED FILTER - multi-layer defense. Only true TWP windows
  // (with real dimensions) reach the cutlist. Notes, doors, adders,
  // ordered windows, and anything missing dimensions are rejected here
  // even if they somehow slipped through upstream classification.
  return (joWindows || [])
    .filter(w => {
      if (!w) return false;
      if (w.lineType !== 'twp_window') return false;
      if (w.lineType === 'note' || w.type === 'note') return false;
      if (w.lineType === 'door' || w.type === 'door') return false;
      if (w.lineType === 'ordered_window' || w.type === 'ordered_window') return false;
      if (w.lineType === 'adder' || w.type === 'adder') return false;
      const width  = parseFloat(w.width);
      const height = parseFloat(w.height);
      if (!width || !height || width <= 0 || height <= 0) return false;
      return true;
    })
    .map(w => {
      counter++;
      const label = contractNumber ? `${contractNumber}-${counter}` : `${counter}`;
      // CRITICAL: width and height MUST be numbers (not strings).
      // The calculation engine does height + 2.25 — string + number = string concat,
      // breaking frame size calculations. Parse to numbers and validate before passing.
      const width  = parseFloat(w.width);
      const height = parseFloat(w.height);
      if (!width || !height || isNaN(width) || isNaN(height)) {
        console.warn(`[cutlist-bridge] Skipping window with invalid dimensions: w=${w.width} h=${w.height}`);
        return null;
      }
      const hasGrids = w.gridColonial || w.gridDiamond || w.gridPrairie;
      const gridPattern = w.gridColonial ? 'Colonial' : w.gridDiamond ? 'Diamond' : w.gridPrairie ? 'Prairie' : '';
      const hasScreen = w.screenFull || w.screenHalf;
      const screenSize = w.screenFull ? 'Full' : 'Half';
      const color = w.colorOutside || w.colorInside || defaultColor || 'White';

      // ── glassPkg must be a plain string like 'DLA', 'DCA', 'TLA' etc ──
      // Never pass an object here — the calculation engine expects a string.
      const glassPkg = (typeof w.glassPkg === 'string' && w.glassPkg)
        ? w.glassPkg
        : (typeof w.glass === 'string' && w.glass)
          ? w.glass
          : 'DLA';

      return {
        id:          Date.now() + Math.random(),
        type:        w.twpType || mapWindowType(w.style || w.description || ''),
        label,
        qty:         parseInt(w.qty) || 1,
        width, height, color,
        config:      w.twpConfig || 'Single',
        handing:     w.handing || 'Hinge Left',
        finType:     w.flanges ? 'With Fin' : 'Without Fin',
        // ── FIX: glassType must be the glass package string, NOT 'DS' ──
        // The calculation engine uses glassType as the primary glass pkg identifier.
        glassType:   glassPkg,
        glassConfig: 'None',
        KeeperGlassPkg:   glassPkg,
        LockGlassPkg:     glassPkg,
        CasementGlassPkg: glassPkg,
        PictureGlassPkg:  glassPkg,
        HopperGlassPkg:   glassPkg,
        gasFill:     'Argon',
        grids:       !!hasGrids,
        gridPattern, gridQtyV: 0, gridQtyH: 0, gridColor: '',
        screen:      !!hasScreen,
        screenSize,
        meshType:    'Standard',
        screenType:  'Flex Scrn',
        options:     w.extrasNotes || '',
        mullCount:   0,
        mullType:    '',
      };
    })
    .filter(w => w !== null);
}

export async function createJobWithBridge(formData) {
  // skipCutlist=true creates ONLY the calendar job (no cutlist).
  // Use this for job orders that have no TWP windows or mulled panes —
  // they still need calendar/production but don't belong in cut list dashboard.
  const skipCutlist = !!formData.skipCutlist;

  // 1. Create TWP scheduler job — status: sold ALWAYS hardcoded
  const twpJobData = {
    customerName: (formData.customerName || '').split('\n')[0].trim(),
    contractNumber: formData.woNumber,
    phoneNumber: formData.phone,
    customerEmail: formData.email,
    address: `${formData.address}, ${formData.city}, ${formData.stateZip}`.trim(),
    name: formData.locationTown || formData.city || formData.customerName,
    windowCount: parseInt(formData.windowCount) || 0,
    doorCount: parseInt(formData.doorCount) || 0,
    durationDays: parseInt(formData.durationDays) || 1,
    jobType: formData.jobType || 'install',
    notes: formData.comments || '',
    soldDate: formData.soldDate ? new Date(formData.soldDate) : new Date(),
    scheduledDate: null,
    status: 'sold',
    statusHistory: [{
      status: 'sold',
      timestamp: new Date(),
      note: 'Job created by office manager'
    }],
    images: [],
    contracts: [],
    workDays: [],
    pdfUrls: {},
    createdAt: serverTimestamp(),
    source: 'office_manager'
  };

  const twpJobRef = await addDoc(collection(db, 'jobs'), twpJobData);
  const twpJobId = twpJobRef.id;

  // 2. Create cutlist job linked to TWP job — UNLESS skipCutlist is set
  let cutlistJobId = null;
  if (!skipCutlist) {
    cutlistJobId = await createCutlistJob({
      ...formData,
      twpJobId,
      status: 'sold',
      createdAt: new Date()
    });

    // 3. Link cutlist job ID back onto TWP job
    await updateDoc(doc(db, 'jobs', twpJobId), { cutlistJobId });
  }

  return { twpJobId, cutlistJobId };
}